import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Maps friendly alias query params to Shopify native filter params.
 * Supports multi-select via repeated params or comma-separated values.
 */
const ALIAS_PARAM_MAPPINGS = [
  // Form (previously Shape)
  { alias: 'pottery_shapes', native: 'filter.p.m.custom.form' },
  { alias: 'pottery_forms', native: 'filter.p.m.custom.form' },
  { alias: 'form', native: 'filter.p.m.custom.form' },
  { alias: 'shape', native: 'filter.p.m.custom.form' },
  // Glaze
  { alias: 'glaze', native: 'filter.v.option.glaze' },
  // Clay (variant option)
  { alias: 'clay', native: 'filter.v.option.clay' },
];

/**
 * Applies alias params on a given URL instance, mutating it to include the
 * corresponding native Shopify facet params and removing the aliases.
 *
 * @param {URL} url
 * @returns {boolean} true if any alias was applied
 */
function applyAliasParams(url) {
  let changed = false;

  for (const { alias, native } of ALIAS_PARAM_MAPPINGS) {
    const aliasValues = url.searchParams.getAll(alias);
    if (!aliasValues.length) continue;

    // Remove the alias to avoid duplication in history/state
    url.searchParams.delete(alias);

    // Normalize and append values to the native facet param
    for (const raw of aliasValues) {
      const splitValues = raw.split(',').map((v) => v.trim()).filter(Boolean);
      for (const value of splitValues) {
        // Avoid duplicate native entries
        const existing = url.searchParams.getAll(native);
        if (!existing.includes(value)) url.searchParams.append(native, value);
      }
    }

    changed = true;
  }

  return changed;
}

/**
 * Toggles a data attribute on the body to indicate grid loading state
 * @param {boolean} isLoading
 */
function setGridLoading(isLoading) {
  try {
    if (isLoading) {
      document.body.setAttribute('data-grid-loading', 'true');
    } else {
      document.body.removeAttribute('data-grid-loading');
    }
  } catch (_) {
    // no-op if document is not available
  }
}

/** Opens the inline filters panel via the same code path as user click */
function openInlineFiltersPanel() {
  // On mobile, there's a separate toggle button with "-mobile" suffix
  // We need to find the correct one based on visibility/viewport
  const isMobile = window.innerWidth < 750;
  
  // Get all toggle buttons
  const allToggles = document.querySelectorAll('.filters-inline__toggle');
  
  // Find the appropriate toggle (mobile or desktop)
  let toggle = null;
  allToggles.forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    const id = btn.id || '';
    if (isMobile && id.includes('-mobile')) {
      toggle = btn;
    } else if (!isMobile && !id.includes('-mobile')) {
      toggle = btn;
    }
  });
  
  // Fallback to first toggle if specific one not found
  if (!toggle) {
    toggle = document.querySelector('.filters-inline__toggle');
  }
  
  if (!(toggle instanceof HTMLElement)) return false;
  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  if (!expanded) toggle.click();
  return true;
}

/**
 * Syncs the aria-expanded state of all filter toggle buttons
 * to match the actual panel visibility state
 */
function syncFilterToggleButtonStates() {
  const panel = document.querySelector('.inline-filters-panel');
  let isOpen = false;
  if (panel instanceof HTMLElement) {
    isOpen = panel.style.display === 'block' || !panel.hidden;
  }
  
  // Update ALL toggle buttons to match the panel state
  document.querySelectorAll('.filters-inline__toggle').forEach((toggle) => {
    if (toggle instanceof HTMLElement) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  });
}

/**
 * All known glaze collection handles (both formats supported)
 * Format 1: glaze-only (e.g. "oat", "dusty-pink") - used on production store
 * Format 2: clay-glaze (e.g. "creme-clay-oat") - used on some dev stores
 */
const ALL_GLAZE_HANDLES = [
  'oat', 'speckled-glossy-white', 'speckled-matte-white', 'speckled-white',
  'glossy-white', 'white-glossy', 'matte-white', 'white-matte',
  'sage-green', 'indigo', 'indigo-blue', 'dusty-pink', 'night-blue'
];

/**
 * Check if a collection handle represents a material/glaze collection
 * @param {string} handle - The collection handle to check
 * @returns {boolean}
 */
function isGlazeCollectionHandle(handle) {
  if (!handle) return false;
  // Check clay-glaze format (e.g. creme-clay-oat)
  if (/^(creme-clay-|dark-clay-)/.test(handle)) return true;
  // Check glaze-only format (e.g. oat)
  return ALL_GLAZE_HANDLES.includes(handle);
}

/**
 * On variant-combo material collection pages (e.g. /collections/oat or /collections/creme-clay-oat),
 * ensure the URL includes the glaze facet param so the material UI behaves like
 * "Shop all" where the active material is represented in the URL.
 *
 * We do NOT force a re-render; we only update the URL via replaceState.
 */
function ensureVariantComboGlazeParamInUrl() {
  try {
    const path = window.location.pathname || '';
    const match = path.match(/\/collections\/([^/?#]+)/);
    const currentHandle = match?.[1] || '';
    
    // Check if this is a material collection page (either format)
    if (!isGlazeCollectionHandle(currentHandle)) return;

    const url = new URL(window.location.href);
    if (url.searchParams.has('filter.v.option.glaze')) return;

    // Prefer the current (aria-current) material pill label to get the exact value.
    const currentPillLink = document.querySelector(
      '.material-filter__rows[data-material-nav="true"] a[aria-current="true"]'
    );
    if (!(currentPillLink instanceof HTMLElement)) return;

    const glazeLabel = (currentPillLink.textContent || '').trim();
    if (!glazeLabel) return;

    url.searchParams.set('filter.v.option.glaze', glazeLabel);
    window.history.replaceState({}, '', url.toString());
  } catch (_) {
    // no-op
  }
}

/**
 * Ensure the material nav pill that corresponds to the current collection handle
 * is visually marked active (.is-active) so it looks "selected" on load and after
 * section re-renders.
 */
function syncVariantComboMaterialNavActiveState() {
  try {
    const path = window.location.pathname || '';
    const match = path.match(/\/collections\/([^/?#]+)/);
    const currentHandle = match?.[1] || '';
    
    // Check if we're on a material collection page (either format)
    const isVariantComboPage = isGlazeCollectionHandle(currentHandle);
    
    // Try multiple selectors - the nav root might be in the inline panel or elsewhere
    const navRoots = document.querySelectorAll('.material-filter__rows[data-material-nav="true"]');
    if (!navRoots.length && !isVariantComboPage) return;

    if (!currentHandle) return;
    
    // Also check for glaze value in URL params
    const url = new URL(window.location.href);
    const glazeParamValue = url.searchParams.get('filter.v.option.glaze') || '';
    
    // Extract glaze handle from clay-glaze format if needed
    let currentGlazeHandle = currentHandle;
    if (currentHandle.startsWith('creme-clay-')) {
      currentGlazeHandle = currentHandle.replace('creme-clay-', '');
    } else if (currentHandle.startsWith('dark-clay-')) {
      currentGlazeHandle = currentHandle.replace('dark-clay-', '');
    }

    // Get all material pills across any container (inline panel, drawer, etc.)
    const allPills = document.querySelectorAll('.material-filter__pill');

    allPills.forEach((pill) => {
      // Check if this pill is a link-based pill (navigation mode)
      const link = pill.querySelector('a[href*="/collections/"]');
      
      // Get glaze value from data attribute
      const glazeValue = pill.getAttribute('data-glaze-value') || '';
      
      if (link instanceof HTMLAnchorElement) {
        // For link-based pills, match by:
        // 1) Glaze value in URL params
        // 2) Current collection handle matching glaze handle
        const matchByGlaze = !!glazeValue && glazeValue === glazeParamValue;
        
        // Also check if glaze label (handleized) matches current handle
        const pillText = (link.textContent || '').trim();
        const pillGlazeHandle = pillText.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        const matchByHandle = pillGlazeHandle === currentGlazeHandle || 
                              pillGlazeHandle === currentHandle ||
                              ALL_GLAZE_HANDLES.some(gh => gh === currentGlazeHandle && (pillGlazeHandle === gh || pillGlazeHandle.includes(gh)));
        
        const isActive = matchByGlaze || matchByHandle;
        
        pill.classList.toggle('is-active', isActive);
        if (isActive) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      } else {
        // For checkbox-based pills (filter mode), check if input is checked
        const input = pill.querySelector('.facets__pill-input:checked');
        if (input) {
          pill.classList.add('is-active');
        }
      }
    });
  } catch (_) {
    // no-op
  }
}

// Ensure the glaze param is present as early as possible on load.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    ensureVariantComboGlazeParamInUrl();
    syncVariantComboMaterialNavActiveState();
  });
} else {
  ensureVariantComboGlazeParamInUrl();
  syncVariantComboMaterialNavActiveState();
}

// If true, we will delay restoring the inline filters open state until after
// the initial filters re-render completes (e.g. glaze-only URL that needs clay).
let __delayInlineOpenUntilAfterRender = false;

/**
 * Store which clay row (by label text) a glaze value should belong to.
 * Used to dedup UI when both clay rows have the same glaze checked on load.
 * @param {string} glazeValue
 * @param {string} clayLabel
 */
function setGlazeClayPreference(glazeValue, clayLabel) {
  try {
    const prefs = JSON.parse(sessionStorage.getItem('__glazeClayPrefs') || '{}');
    prefs[glazeValue] = clayLabel;
    sessionStorage.setItem('__glazeClayPrefs', JSON.stringify(prefs));
  } catch (_) {}
}

/**
 * Get the preferred clay row for a glaze value.
 * @param {string} glazeValue
 * @returns {string}
 */
function getGlazeClayPreference(glazeValue) {
  try {
    const prefs = JSON.parse(sessionStorage.getItem('__glazeClayPrefs') || '{}');
    return prefs[glazeValue] || '';
  } catch (_) {
    return '';
  }
}

/**
 * Reconciles the Material (Clay/Glaze) filter UI so that glazes which exist
 * in multiple clay rows (e.g. white glazes) are not simultaneously selected
 * in more than one row after a server render.
 */
function reconcileMaterialFilterState() {
  // Check if we're in nav mode (variant-combo collection page)
  const isNavMode = !!document.querySelector('.material-filter__rows[data-material-nav="true"]');
  
  // Process each filter form independently so we don't accidentally
  // uncheck selections in one form because the same glaze exists in another.
  document.querySelectorAll('facets-form-component').forEach((formScope) => {
    // No UI de-duplication on load: allow the same glaze to be selected
    // in both clay rows and preserve it across refresh.

    // 1) Update visual active state per row within this form
    formScope.querySelectorAll('.material-filter__row').forEach((row) => {
      // In nav mode (link-based pills), preserve server-rendered .is-active
      // or sync based on URL/handle match - don't remove based on checkboxes
      const rowHasLinkPills = !!row.querySelector('.material-filter__pill > a[href*="/collections/"]');
      
      if (rowHasLinkPills) {
        // For link-based pills, don't touch the .is-active class here;
        // let syncVariantComboMaterialNavActiveState() handle it
        return;
      }
      
      // For checkbox-based pills, sync .is-active with checked state
      row.querySelectorAll('.material-filter__pill.is-active').forEach((p) => p.classList.remove('is-active'));
      row.querySelectorAll('.material-filter__pill .facets__pill-input:checked').forEach((input) => {
        const pill = input.closest('.material-filter__pill');
        if (pill) pill.classList.add('is-active');
      });
    });

    // 2) Sync the hidden clay checkbox for each row within this form (only for filter mode)
    if (!isNavMode) {
      formScope.querySelectorAll('.material-filter__row').forEach((row) => {
        const clayInput = row.querySelector('input[type="checkbox"][data-clay-input]');
        if (clayInput instanceof HTMLInputElement) {
          clayInput.checked = !!row.querySelector('[data-material-glaze-input]:checked');
        }
      });
    }
  });
  
  // After reconciliation, sync the nav mode active state
  if (isNavMode) {
    syncVariantComboMaterialNavActiveState();
  }
}

/**
 * Remove a single occurrence of a param=value pair from URLSearchParams
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @param {string} value
 */
function removeOne(searchParams, key, value) {
  const values = searchParams.getAll(key);
  if (!values.length) return;
  searchParams.delete(key);
  let removed = false;
  for (const v of values) {
    if (!removed && v === value) {
      removed = true;
      continue;
    }
    searchParams.append(key, v);
  }
}

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Whether an element lives inside the filters drawer (stale mirror UI).
 * @param {Element} el
 * @returns {boolean}
 */
function isFacetFormInDrawer(el) {
  return !!el.closest('#filters-drawer, dialog.facets--drawer, .dialog-drawer');
}

/**
 * Aggregates filter.* and sort_by values from all live (non-drawer) facet forms.
 * Sort and Filters live in separate <form> elements, so a change in one form
 * must borrow the other form's active state or it will wipe it from the URL.
 *
 * @param {{ includeClay?: boolean }} [options]
 * @returns {URLSearchParams}
 */
function collectLiveFacetParameters({ includeClay = false } = {}) {
  const params = new URLSearchParams();

  document.querySelectorAll('facets-form-component form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (isFacetFormInDrawer(form)) return;

    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (typeof key !== 'string') continue;
      const stringValue = String(value);

      if (key === 'sort_by') {
        if (stringValue) params.set('sort_by', stringValue);
        continue;
      }

      if (!key.startsWith('filter.')) continue;
      if (!includeClay && key === 'filter.v.option.clay') continue;
      if (
        (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') &&
        stringValue.trim() === ''
      ) {
        continue;
      }

      const existing = params.getAll(key);
      if (!existing.includes(stringValue)) params.append(key, stringValue);
    }
  });

  return params;
}

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  connectedCallback() {
    super.connectedCallback();
    // Apply alias params only once per load
    if (this.dataset.aliasApplied !== 'true') {
      const url = new URL(window.location.href);
      if (applyAliasParams(url)) {
        this.dataset.aliasApplied = 'true';
        // This will update URL, dispatch FilterUpdateEvent and re-render section
        this.updateFiltersByURL(url.toString());
      }
    }

    // Persist the section id that originally hosts this UI (filters block)
    // so that subsequent renders still refresh the correct section even if
    // the panel has been moved into the portal under the results section.
    const hostingUiSectionId = this.getAttribute('ui-section-id');
    if (!hostingUiSectionId) {
      const uiSection = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
      if (uiSection) this.setAttribute('ui-section-id', uiSection);
    }

    // Ensure the component always points at the actual results section.
    // In some layouts the filter block lives in a separate section from the
    // product grid, so we resolve the grid section once here and pin it in an
    // attribute for subsequent renders (works outside the theme editor too).
    const explicitId = this.getAttribute('section-id');
    const explicitEl = explicitId ? document.getElementById(`shopify-section-${explicitId}`) : null;
    const looksLikeResults = explicitEl && (explicitEl.querySelector('results-list') || explicitEl.querySelector('.product-grid-container') || explicitEl.querySelector('.product-grid'));
    if (!looksLikeResults) {
      const resultsListEl = document.querySelector('results-list');
      const resultsListId = resultsListEl?.getAttribute('section-id');
      if (resultsListId) this.setAttribute('section-id', resultsListId);
      else {
        const gridContainer = document.querySelector('.product-grid-container, .product-grid');
        const gridSectionId = gridContainer?.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
        if (gridSectionId) this.setAttribute('section-id', gridSectionId);
      }
    }
  }

  /**
   * Merge filter/sort state from sibling facet forms when this form is incomplete.
   * The Sort control and the Filters panel are separate forms in new-filters.liquid.
   *
   * @param {URLSearchParams} parameters
   * @param {{ includeClay?: boolean }} [options]
   * @returns {URLSearchParams}
   */
  #mergeSiblingFacetParameters(parameters, { includeClay = false } = {}) {
    const formEl = this.refs.facetsForm;
    const formHasFilterInputs = !!formEl?.querySelector?.('[name^="filter."]');
    const formHasSortInput = !!formEl?.querySelector?.('[name="sort_by"]');
    const liveParams = collectLiveFacetParameters({ includeClay });

    // Sort-only forms have no filter inputs — borrow active filters from the inline panel.
    if (!formHasFilterInputs) {
      for (const [key, value] of liveParams.entries()) {
        if (!key.startsWith('filter.')) continue;
        const existing = parameters.getAll(key);
        if (!existing.includes(value)) parameters.append(key, value);
      }
    }

    // Filter-only forms have no sort input — keep the currently selected sort.
    if (!formHasSortInput || !parameters.get('sort_by')) {
      const sortBy =
        liveParams.get('sort_by') || new URL(window.location.href).searchParams.get('sort_by');
      if (sortBy) parameters.set('sort_by', sortBy);
    }

    return parameters;
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));
    newParameters = this.#mergeSiblingFacetParameters(newParameters, { includeClay: false });

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Don't persist Clay in the URL; we only want Glaze persisted to avoid
    // duplicated chips / disabled state after refresh
    if (newParameters.has('filter.v.option.clay')) newParameters.delete('filter.v.option.clay');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Creates URL parameters for Section Rendering requests. Unlike
   * createURLParameters, this preserves Clay so the server filters by
   * Clay + Glaze, while the browser URL remains glaze-only.
   * @param {FormData} [formData]
   * @returns {URLSearchParams}
   */
  createRequestURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));
    newParameters = this.#mergeSiblingFacetParameters(newParameters, { includeClay: true });

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    // Keep clay here on purpose so the request includes it
    newParameters.delete('page');

    // De-duplicate glaze values: keep only the first occurrence of each
    // (since both clay rows might have the same glaze checked, but we only send one)
    const seenGlazes = new Set();
    for (const value of newParameters.getAll('filter.v.option.glaze')) {
      seenGlazes.add(value);
    }
    newParameters.delete('filter.v.option.glaze');
    for (const value of seenGlazes) {
      newParameters.append('filter.v.option.glaze', value);
    }

    // If no glaze values are selected, ensure clay is not sent at all.
    // This prevents getting "stuck" on clay after unselecting the last glaze.
    if (!newParameters.has('filter.v.option.glaze')) {
      newParameters.delete('filter.v.option.clay');
    }

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    // Prefer explicit attribute when it points at the main results section.
    // If it points at a different section (e.g. a header section that hosts
    // the filters block), fall back to the page's primary results-list.
    const explicitId = this.getAttribute('section-id');
    if (explicitId) {
      const explicitEl = document.getElementById(`shopify-section-${explicitId}`);
      const looksLikeResults = explicitEl && (explicitEl.querySelector('results-list') || explicitEl.querySelector('.product-grid-container'));
      if (looksLikeResults) return explicitId;
    }

    const resultsListEl = document.querySelector('results-list');
    const resultsListId = resultsListEl?.getAttribute('section-id');
    if (resultsListId) return resultsListId;

    const closestSection = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
    if (closestSection) return closestSection;

    throw new Error('Section ID is required');
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    // 1) Update URL/history with browser params (without clay)
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));

    // 2) Delegate rendering to the unified section updater which
    //    handles Clay/Glaze reconciliation and UI+grid refresh.
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');
    setGridLoading(true);
    // Capture open state before render so we can re-apply to the new controls
    const wasOpen = !!document.querySelector('.filters-inline__toggle[aria-expanded="true"]');
    // Also capture the section that hosts the UI (filters block) so we can refresh
    // its counts/disabled states alongside the product grid section. Otherwise the
    // server-updated counts are not reflected in the header block after changes.
    const uiSectionId = this.getAttribute('ui-section-id') || this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
    // Only re-render the header UI during the very first reconciliation pass
    // (when we land with glaze in the URL). For interactive toggles we keep
    // the existing UI in place so per-row selections remain independent.
    const needsUIRerender = __delayInlineOpenUntilAfterRender && uiSectionId && uiSectionId !== this.sectionId;

    // Build a request URL that includes Clay, even though browser URL
    // remains glaze-only. Use form state (hidden clay inputs) for accuracy.
    const requestParameters = this.createRequestURLParameters();
    // Safety: if no clay made it into the request but there are selected glazes
    // in any material row (including sibling filter forms), append clay so the
    // server filters by Clay + Glaze.
    if (!requestParameters.has('filter.v.option.clay')) {
      document.querySelectorAll('.material-filter__row').forEach((row) => {
        if (!(row instanceof HTMLElement) || isFacetFormInDrawer(row)) return;
        const anyCheckedGlazeInRow = !!row.querySelector?.('[data-material-glaze-input]:checked');
        if (!anyCheckedGlazeInRow) return;
        const clayInput = row.querySelector?.('input[type="checkbox"][data-clay-input]');
        if (clayInput instanceof HTMLInputElement) {
          const existing = requestParameters.getAll('filter.v.option.clay');
          if (!existing.includes(clayInput.value)) {
            requestParameters.append('filter.v.option.clay', clayInput.value);
          }
        }
      });
    }

    // Always build the section-render URL from the merged request params.
    // Previously, sort-only updates skipped this path when no filters were present,
    // so sort_by never reached the server even though it appeared in the browser URL.
    let requestUrl = new URL(this.refs.facetsForm?.action || window.location.href, window.location.origin);
    requestUrl.search = '';
    for (const [param, value] of requestParameters.entries()) {
      requestUrl.searchParams.append(param, value);
    }
    requestUrl.searchParams.delete('page');

    // Remove any stale inline panel from the portal before re-rendering.
    // When glaze is in the URL, this method is called to reconcile clay/glaze.
    // If the panel was already moved into the portal, the re-render will create
    // a fresh copy in the header. Without cleanup, both will coexist (ghost + real).
    // Use the original UI section id (not the results section id) to locate
    // and clean up any stale, previously-moved inline panel in the portal.
    const headerSectionId = this.getAttribute('ui-section-id') || this.getAttribute('section-id');
    if (headerSectionId) {
      const portal = document.querySelector('.inline-filters-portal');
      if (portal) {
        const stalePanelId = `InlineFiltersPanel--${headerSectionId}`;
        const stalePanel = portal.querySelector(`#${stalePanelId}`);
        // Do NOT remove the visible panel while open; wait until after render
        if (stalePanel && !wasOpen) {
          stalePanel.remove();
        }
      }
    }

    if (viewTransition) {
      startViewTransition(() => {
        const renders = [sectionRenderer.renderSection(this.sectionId, { url: requestUrl, cache: false })];
        if (needsUIRerender) renders.push(sectionRenderer.renderSection(/** @type {string} */ (uiSectionId), { url: requestUrl, cache: false }));
        return Promise.all(renders);
      }, ['product-grid']).finally(() => {
      // Immediately re-parent the new panel to the portal and restore open state
      // Do this BEFORE reconciliation so the panel is in the right place from the start
      if (wasOpen) {
        const toggle = document.querySelector('.filters-inline__toggle');
        const panelId = toggle?.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        
        if (toggle instanceof HTMLElement && panel instanceof HTMLElement) {
          // Move panel to portal FIRST (before setting display)
          const portal = document.querySelector('.inline-filters-portal');
          if (portal instanceof HTMLElement) {
            // Remove any old panels in the portal first
            const oldPanels = portal.querySelectorAll('[id^="InlineFiltersPanel--"]');
            oldPanels.forEach(p => {
              if (p.id !== panel.id) p.remove();
            });
            // Move the new panel into the portal if not already there
            if (!portal.contains(panel)) {
              portal.appendChild(panel);
            }
          }
          // Now set the toggle and panel states
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          panel.style.display = 'block';
          const rootFacets = toggle.closest('.facets');
          if (rootFacets instanceof HTMLElement) {
            rootFacets.classList.add('inline-filters-open');
          }
          // Sync ALL toggle buttons (both mobile and desktop)
          syncFilterToggleButtonStates();
          // Also add body class for Safari overflow fix
          document.body.classList.add('inline-filters-open');
          document.documentElement.classList.add('inline-filters-open');
        }
      }
        // After the new HTML has been morphed into place, reconcile duplicate glaze selections
        reconcileMaterialFilterState();
      // If we delayed restoring the panel open state due to glaze-only URL,
      // do it now after the first render.
      if (__delayInlineOpenUntilAfterRender) {
        __delayInlineOpenUntilAfterRender = false;
        const shouldOpen = sessionStorage.getItem('inlineFiltersOpen') === 'true';
        if (shouldOpen) openInlineFiltersPanel();
      }
        // Remove duplicate inline filter panels from portal (post-render sanity check)
        const portal = document.querySelector('.inline-filters-portal');
        if (portal) {
          const allPanels = portal.querySelectorAll('[id^="InlineFiltersPanel--"]');
          // Keep only the last (most recently rendered) panel for each unique section ID
          const seenIds = /** @type {Record<string, Element>} */ ({});
          // First pass: mark the last occurrence of each ID
          allPanels.forEach((panel) => {
            seenIds[panel.id] = panel;
          });
          // Second pass: remove all that aren't the last one
          allPanels.forEach((panel) => {
            if (seenIds[panel.id] !== panel) {
              panel.remove();
            }
          });
        }
        // Update Reset pill visibility after re-render (covers first reconciliation pass)
        try { setResetPillVisibility(hasActiveFiltersInForms()); } catch (_) {}
        // Sync form filter active state after re-render
        try { syncFormFilterActiveState(); } catch (_) {}
        setGridLoading(false);
      });
    } else {
      const renders = [sectionRenderer.renderSection(this.sectionId, { url: requestUrl, cache: false })];
      if (needsUIRerender) renders.push(sectionRenderer.renderSection(/** @type {string} */ (uiSectionId), { url: requestUrl, cache: false }));
      Promise.all(renders).finally(() => {
      // Immediately re-parent the new panel to the portal and restore open state
      // Do this BEFORE reconciliation so the panel is in the right place from the start
      if (wasOpen) {
        const toggle = document.querySelector('.filters-inline__toggle');
        const panelId = toggle?.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        
        if (toggle instanceof HTMLElement && panel instanceof HTMLElement) {
          // Move panel to portal FIRST (before setting display)
          const portal = document.querySelector('.inline-filters-portal');
          if (portal instanceof HTMLElement) {
            // Remove any old panels in the portal first
            const oldPanels = portal.querySelectorAll('[id^="InlineFiltersPanel--"]');
            oldPanels.forEach(p => {
              if (p.id !== panel.id) p.remove();
            });
            // Move the new panel into the portal if not already there
            if (!portal.contains(panel)) {
              portal.appendChild(panel);
            }
          }
          // Now set the toggle and panel states
          toggle.setAttribute('aria-expanded', 'true');
          panel.hidden = false;
          panel.style.display = 'block';
          const rootFacets = toggle.closest('.facets');
          if (rootFacets instanceof HTMLElement) {
            rootFacets.classList.add('inline-filters-open');
          }
          // Sync ALL toggle buttons (both mobile and desktop)
          syncFilterToggleButtonStates();
          // Also add body class for Safari overflow fix
          document.body.classList.add('inline-filters-open');
          document.documentElement.classList.add('inline-filters-open');
        }
      }
        reconcileMaterialFilterState();
        if (__delayInlineOpenUntilAfterRender) {
          __delayInlineOpenUntilAfterRender = false;
          const shouldOpen = sessionStorage.getItem('inlineFiltersOpen') === 'true';
          if (shouldOpen) openInlineFiltersPanel();
        }
        // Remove duplicate inline filter panels from portal (post-render sanity check)
        const portal = document.querySelector('.inline-filters-portal');
        if (portal) {
          const allPanels = portal.querySelectorAll('[id^="InlineFiltersPanel--"]');
          // Keep only the last (most recently rendered) panel for each unique section ID
          const seenIds = /** @type {Record<string, Element>} */ ({});
          // First pass: mark the last occurrence of each ID
          allPanels.forEach((panel) => {
            seenIds[panel.id] = panel;
          });
          // Second pass: remove all that aren't the last one
          allPanels.forEach((panel) => {
            if (seenIds[panel.id] !== panel) {
              panel.remove();
            }
          });
        }
        try { setResetPillVisibility(hasActiveFiltersInForms()); } catch (_) {}
        // Sync form filter active state after re-render
        try { syncFormFilterActiveState(); } catch (_) {}
        setGridLoading(false);
      });
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

// Inline Filters controller: toggles the inline panel under the header
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  // Inline filters close button → use same logic as the toggle button
  const closeBtn = event.target.closest('.filters-inline__close');
  if (closeBtn) {
    const toggleBtn = document.querySelector('.filters-inline__toggle');
    if (toggleBtn instanceof HTMLElement) toggleBtn.click();
    return;
  }
  // Global Reset pill: clear all filters using existing section rendering flow
  const resetBtn = event.target.closest('.facets-reset-pill');
  if (resetBtn) {
    try {
      const removeWrapper = resetBtn.closest('facet-remove-component');
      const resultsUrl = removeWrapper?.getAttribute('data-url') || resetBtn.getAttribute('data-url') || window.location.pathname;
      // 1) Clear all local UI selections across all filter forms so the panel reflects reset immediately
      document.querySelectorAll('facets-form-component').forEach((formComponent) => {
        const formEl = formComponent.querySelector('form');
        if (!(formEl instanceof HTMLFormElement)) return;
        // Uncheck all filter inputs (checkboxes/radios) and clear price fields
        formEl.querySelectorAll('input[name^="filter."]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          if (input.type === 'checkbox' || input.type === 'radio') input.checked = false;
          else input.value = '';
        });
        // Remove active state from material pills and uncheck hidden clay inputs
        formEl.querySelectorAll('.material-filter__pill.is-active').forEach((pill) => {
          pill.classList.remove('is-active');
        });
        formEl.querySelectorAll('input[type="checkbox"][data-clay-input]').forEach((clay) => {
          if (clay instanceof HTMLInputElement) clay.checked = false;
        });
      });
      // Hide the reset pill immediately to avoid flicker
      if (resetBtn instanceof HTMLElement) resetBtn.style.display = 'none';
      // 2) Trigger a server render back to the base results URL (no filters)
      const formComponent = document.querySelector('facets-form-component');
      if (formComponent && typeof /** @type any */ (formComponent).updateFiltersByURL === 'function') {
        /** @type any */ (formComponent).updateFiltersByURL(resultsUrl);
      }
    } catch (_) {}
    return;
  }
  const toggle = event.target.closest('.filters-inline__toggle');
  if (!(toggle instanceof HTMLElement)) return;

  const controlsId = toggle.getAttribute('aria-controls') || '';
  const panel = document.getElementById(controlsId);
  if (!(panel instanceof HTMLElement)) return;

  const expanded = toggle.getAttribute('aria-expanded') === 'true';
  const newState = !expanded;
  toggle.setAttribute('aria-expanded', newState.toString());
  panel.hidden = expanded;

  // Persist current open state across refresh within this session
  try {
    sessionStorage.setItem('inlineFiltersOpen', newState.toString());
  } catch (_) {}

  // Also force visibility to ensure CSS isn't blocking it and move panel below the grid into portal
  const rootFacets = toggle.closest('.facets');
  const portal = document.querySelector('.inline-filters-portal');
  
  if (rootFacets instanceof HTMLElement) {
    // Desktop case: toggle is inside .facets
    if (expanded) {
      rootFacets.classList.remove('inline-filters-open');
      panel.hidden = true;
      panel.style.display = 'none';
      // SAFARI FIX: Remove body class to re-enable horizontal scroll
      document.body.classList.remove('inline-filters-open');
      document.documentElement.classList.remove('inline-filters-open');
    } else {
      rootFacets.classList.add('inline-filters-open');
      panel.hidden = false;
      panel.style.display = 'block';
      if (portal instanceof HTMLElement && !portal.contains(panel)) {
        portal.appendChild(panel);
      }
      // SAFARI FIX: Add body class to prevent horizontal scroll
      document.body.classList.add('inline-filters-open');
      document.documentElement.classList.add('inline-filters-open');
      // Sync form filter active state when panel opens
      syncFormFilterActiveState();
    }
  } else {
    // Mobile case: toggle is in .facets-toggle, not inside .facets
    if (expanded) {
      panel.hidden = true;
      panel.style.display = 'none';
      // Remove class from collection wrapper if it exists
      const collectionWrapper = toggle.closest('.collection-wrapper');
      if (collectionWrapper instanceof HTMLElement) {
        collectionWrapper.classList.remove('inline-filters-open');
      }
      // SAFARI FIX: Remove body class to re-enable horizontal scroll
      document.body.classList.remove('inline-filters-open');
      document.documentElement.classList.remove('inline-filters-open');
    } else {
      panel.hidden = false;
      panel.style.display = 'block';
      // Add class to collection wrapper for CSS targeting
      const collectionWrapper = toggle.closest('.collection-wrapper');
      if (collectionWrapper instanceof HTMLElement) {
        collectionWrapper.classList.add('inline-filters-open');
      }
      // Move panel to portal if it exists
      if (portal instanceof HTMLElement && !portal.contains(panel)) {
        portal.appendChild(panel);
      }
      // SAFARI FIX: Add body class to prevent horizontal scroll
      document.body.classList.add('inline-filters-open');
      document.documentElement.classList.add('inline-filters-open');
      // Sync form filter active state when panel opens (mobile)
      // Run immediately and with delays to catch any DOM updates
      syncFormFilterActiveState();
      setTimeout(syncFormFilterActiveState, 50);
      setTimeout(syncFormFilterActiveState, 150);
    }
  }
});

// On initial page load, restore inline filters open state if saved
document.addEventListener('DOMContentLoaded', () => {
  try {
    const shouldOpen = sessionStorage.getItem('inlineFiltersOpen') === 'true';
    if (!shouldOpen) return;
    if (__delayInlineOpenUntilAfterRender) return; // wait until after first render when glaze-only URL

    // Use the same code path as a user click to avoid partial UI state
    openInlineFiltersPanel();
    
    // Ensure both toggle buttons have correct aria-expanded state
    setTimeout(() => {
      syncFilterToggleButtonStates();
    }, 50);
  } catch (_) {}
});

// After any filter-driven section re-render, the inline panel DOM is replaced,
// so re-sync the active material nav pill.
document.addEventListener('theme:filter:update', syncVariantComboMaterialNavActiveState);
document.addEventListener('filter:update', syncVariantComboMaterialNavActiveState);
document.addEventListener('FilterUpdate', syncVariantComboMaterialNavActiveState);

// Toggle visibility of Reset pill based on whether any filters are active
function setResetPillVisibility(visible) {
  // Desktop reset pills
  document.querySelectorAll('.facets-reset-pill').forEach((el) => {
    if (el instanceof HTMLElement) el.style.display = visible ? 'inline-flex' : 'none';
  });
  
  // Mobile reset wrapper (uses class toggle for CSS control)
  document.querySelectorAll('.facets-reset-mobile-wrapper').forEach((el) => {
    if (el instanceof HTMLElement) {
      el.classList.toggle('is-visible', visible);
    }
  });
}

function hasActiveFiltersInForms() {
  let anyActive = false;

  // Helper: ignore forms inside the drawer/dialog (stale mirror of inline filters)
  const isInDrawer = (el) => !!el.closest('#filters-drawer, .dialog, dialog, .dialog-drawer');

  // Fast path: any glaze selected in visible (non-drawer) forms
  const visibleGlazeChecked = Array.from(document.querySelectorAll('[data-material-glaze-input]:checked'))
    .some((input) => !isInDrawer(input));
  if (visibleGlazeChecked) return true;

  document.querySelectorAll('facets-form-component form').forEach((form) => {
    if (!(form instanceof HTMLFormElement)) return;
    if (isInDrawer(form)) return; // Skip the drawer mirror

    // Any non-clay facet input selected (checkboxes/radios)
    if (form.querySelector('[name^="filter."][type="checkbox"]:checked:not([name="filter.v.option.clay"]), [name^="filter."][type="radio"]:checked:not([name="filter.v.option.clay"])')) {
      anyActive = true;
      return;
    }
    const min = form.querySelector('input[name="filter.v.price.gte"]');
    const max = form.querySelector('input[name="filter.v.price.lte"]');
    if ((min instanceof HTMLInputElement && min.value.trim() !== '') || (max instanceof HTMLInputElement && max.value.trim() !== '')) {
      anyActive = true;
      return;
    }
  });
  return anyActive;
}

document.addEventListener(ThemeEvents.FilterUpdate, (/** @type any */ event) => {
  const fromEvent = !!event && typeof event.shouldShowClearAll === 'function' ? event.shouldShowClearAll() : false;
  // Prefer event signal; fall back to DOM state (covers clay-only states)
  setResetPillVisibility(fromEvent || hasActiveFiltersInForms());
});

document.addEventListener('DOMContentLoaded', () => {
  // On initial load, the browser URL might not include all filter params (e.g., clay-only state).
  // Detect active selections directly from the forms to decide visibility.
  setResetPillVisibility(hasActiveFiltersInForms());
  // Re-check after the rest of theme initialisation completes
  try {
    if (typeof requestIdleCallback === 'function') requestIdleCallback(() => setResetPillVisibility(hasActiveFiltersInForms()));
    else setTimeout(() => setResetPillVisibility(hasActiveFiltersInForms()), 0);
  } catch (_) {}
});

// Final safety: once all resources are loaded, re-evaluate (covers late DOM updates)
window.addEventListener('load', () => setResetPillVisibility(hasActiveFiltersInForms()));

// Combined Material filter (clay→glaze) support
// Ensure the row's hidden clay input is checked when any glaze in that row is toggled.
// Use capture so this runs before other change handlers update the section.
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches('[data-material-glaze-input]')) return;

    const row = target.closest('.material-filter__row');
    if (!(row instanceof HTMLElement)) return;

    const clayInput = row.querySelector('input[type="checkbox"][data-clay-input]');
    if (!(clayInput instanceof HTMLInputElement)) return;

    const anyChecked = !!row.querySelector('[data-material-glaze-input]:checked');
    clayInput.checked = anyChecked;

    // Record preference: which clay row this glaze was selected in
    if (target.checked) {
      const clayLabel = row.querySelector('.material-filter__label')?.textContent?.trim();
      if (clayLabel) {
        setGlazeClayPreference(target.value, clayLabel);
      }
    }

    // Do NOT persist clay facet in the URL; we want only glaze in URL params
    // to avoid duplicate chips/locking after refresh.
    const url = new URL(window.location.href);
    url.searchParams.delete('filter.v.option.clay');
    history.replaceState({}, '', url.toString());

    // Update visual active state immediately on the pill wrapper
    const pill = target.closest('.material-filter__pill');
    if (pill instanceof HTMLElement) {
      if (target.checked) pill.classList.add('is-active');
      else pill.classList.remove('is-active');
    }
    // Immediately reflect reset-pill visibility on any glaze toggle
    try { setResetPillVisibility(hasActiveFiltersInForms()); } catch (_) {}
  },
  true
);

// Keep Reset pill in sync for any facet input change (checkboxes, radios, price)
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!(typeof target.name === 'string' && target.name.startsWith('filter.'))) return;
    try { setResetPillVisibility(hasActiveFiltersInForms()); } catch (_) {}
  },
  true
);

// Make clicking the pill label itself toggle the checkbox and update state immediately
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const label = event.target.closest('.material-filter__pill-label');
  if (!label) return;

  const wrapper = label.closest('.material-filter__pill');
  const input = wrapper?.querySelector('.facets__pill-input');
  if (!(input instanceof HTMLInputElement)) return;

  // Avoid double toggles if the input receives the native label click
  event.preventDefault();
  event.stopPropagation();

  input.checked = !input.checked;
  const changeEvent = new Event('change', { bubbles: true });
  input.dispatchEvent(changeEvent);
});

// Close any open material info popovers when clicking outside
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const targetEl = /** @type {Element} */ (event.target);
  const openInfos = document.querySelectorAll('.material-info[open]');
  openInfos.forEach((info) => {
    if (!info.contains(targetEl)) {
      info.removeAttribute('open');
    }
  });
});

// Also close info on Escape
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.material-info[open]').forEach((info) => info.removeAttribute('open'));
});

/**
 * Sync the active state of Form filter images (Plates, Bowls, etc.)
 * based on the current collection URL.
 */
function syncFormFilterActiveState() {
  try {
    const path = window.location.pathname || '';
    const match = path.match(/\/collections\/([^/?#]+)/);
    const currentHandle = match?.[1]?.toLowerCase() || '';
    
    if (!currentHandle) return;
    
    // Find all form filter images in the inline panel
    const formFilterItems = document.querySelectorAll('.inline-filters-panel .variant-option--images');
    
    formFilterItems.forEach((item) => {
      // Get the link or label that contains the image
      const link = item.querySelector('a.facets__image-wrapper, label.facets__image-wrapper');
      const label = item.querySelector('.facets__image-label');
      
      if (!link || !label) return;
      
      // Get the form name from the label text
      const formName = (label.textContent || '').trim();
      const formHandle = formName.toLowerCase().replace(/\s+&\s+/g, '-').replace(/\s+/g, '-');
      
      // Check if this form matches the current collection
      // Handle various naming conventions (e.g., "mugs-tumblers" vs "mugs-&-tumblers")
      const formHandleFirstPart = formHandle.split('-')[0] || '';
      const isActive = currentHandle === formHandle || 
                       currentHandle === formHandle.replace(/-/g, '') ||
                       (formHandleFirstPart && currentHandle.includes(formHandleFirstPart));
      
      // Also check if the href matches the current page
      const href = link.getAttribute('href') || '';
      const hrefMatch = href.match(/\/collections\/([^/?#]+)/);
      const hrefHandle = hrefMatch?.[1]?.toLowerCase() || '';
      const isActiveByHref = hrefHandle && currentHandle === hrefHandle;
      
      // Get parent list item for additional class targeting
      const listItem = item.closest('.facets__inputs-list-item');
      
      if (isActive || isActiveByHref) {
        // Add active class to the image wrapper
        link.classList.add('facets__image-wrapper--active');
        link.setAttribute('aria-current', 'true');
        // Also add class to parent elements for more robust CSS targeting
        if (item instanceof HTMLElement) {
          item.classList.add('is-active-form');
        }
        if (listItem instanceof HTMLElement) {
          listItem.classList.add('is-active-form');
        }
      } else {
        link.classList.remove('facets__image-wrapper--active');
        link.removeAttribute('aria-current');
        if (item instanceof HTMLElement) {
          item.classList.remove('is-active-form');
        }
        if (listItem instanceof HTMLElement) {
          listItem.classList.remove('is-active-form');
        }
      }
    });
  } catch (_) {
    // no-op
  }
}

// Run on DOMContentLoaded and after any filter updates
document.addEventListener('DOMContentLoaded', syncFormFilterActiveState);
document.addEventListener('theme:filter:update', syncFormFilterActiveState);
document.addEventListener('filter:update', syncFormFilterActiveState);

// Also run after delays to catch any late renders (especially important for mobile)
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(syncFormFilterActiveState, 100);
  setTimeout(syncFormFilterActiveState, 300);
  setTimeout(syncFormFilterActiveState, 500);
  setTimeout(syncFormFilterActiveState, 1000);
});

// Run when page becomes visible (e.g., after navigation)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    setTimeout(syncFormFilterActiveState, 100);
  }
});

// On load, set .is-active only for the pills whose matching clay row is present
document.addEventListener('DOMContentLoaded', () => {
  // On load, strip clay from URL so only glaze persists across refreshes
  const url = new URL(window.location.href);
  if (url.searchParams.has('filter.v.option.clay')) {
    url.searchParams.delete('filter.v.option.clay');
    history.replaceState({}, '', url.toString());
  }
  // If URL contains glaze but no clay, the theme will do one reconciliation
  // render to include clay for the request. During that brief moment, opening
  // the inline panel too early can show an empty/duplicate panel. Defer it.
  __delayInlineOpenUntilAfterRender = url.searchParams.has('filter.v.option.glaze');
  
  // Check if we're in nav mode (variant-combo collection)
  const isNavMode = !!document.querySelector('.material-filter__rows[data-material-nav="true"]');
  
  document.querySelectorAll('.material-filter__row').forEach((row) => {
    // For link-based pills (nav mode), DON'T clear .is-active - preserve server state
    const hasLinkPills = !!row.querySelector('.material-filter__pill > a[href*="/collections/"]');
    
    if (!hasLinkPills) {
      // Clear all is-active in this row (checkbox mode only)
      row.querySelectorAll('.material-filter__pill.is-active').forEach((p) => p.classList.remove('is-active'));
      // Add is-active to checked items in this row only
      row.querySelectorAll('.material-filter__pill .facets__pill-input:checked').forEach((input) => {
        const pill = input.closest('.material-filter__pill');
        if (pill) pill.classList.add('is-active');
      });
    }
    
    // Ensure the hidden clay checkbox mirrors glaze selection on load (only in filter mode)
    if (!isNavMode) {
      const clayInput = row.querySelector('input[type="checkbox"][data-clay-input]');
      if (clayInput instanceof HTMLInputElement) {
        const anyCheckedGlazeInRow = !!row.querySelector('[data-material-glaze-input]:checked');
        clayInput.checked = anyCheckedGlazeInRow;
      }
    }
  });

  // Realign hidden clay inputs to current per-row selection (only in filter mode)
  if (!isNavMode) {
    document.querySelectorAll('.material-filter__row').forEach((row) => {
      const clayInput = row.querySelector('input[type="checkbox"][data-clay-input]');
      if (clayInput instanceof HTMLInputElement) {
        clayInput.checked = !!row.querySelector('[data-material-glaze-input]:checked');
      }
    });
  }

  // Also reconcile duplicate selections on initial load
  reconcileMaterialFilterState();
});

// Post-paint reconciliation: if the URL has no Clay param but server rendered
// any Clay inputs as checked, uncheck them and re-render once to align state.
document.addEventListener('DOMContentLoaded', () => {
  const url = new URL(window.location.href);
  const hasClayParam = url.searchParams.has('filter.v.option.clay');

  // First, de-duplicate any glaze selections on load WITHIN EACH FORM
  // Both inline panel and drawer forms may exist; deduplicate independently
  document.querySelectorAll('facets-form-component').forEach((formComponent) => {
    const seenGlazeValuesPerForm = new Set();
    formComponent.querySelectorAll('[data-material-glaze-input]:checked').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const value = input.value;
      if (seenGlazeValuesPerForm.has(value)) {
        input.checked = false;
        const pill = input.closest('.material-filter__pill');
        if (pill instanceof HTMLElement) pill.classList.remove('is-active');
      } else {
        seenGlazeValuesPerForm.add(value);
      }
    });
  });

  // Align hidden clay inputs to current glaze selection on load
  let anyRowHasGlaze = false;
  document.querySelectorAll('.material-filter__row').forEach((row) => {
    const clayInput = row.querySelector('input[type="checkbox"][data-clay-input]');
    const anyCheckedGlazeInRow = !!row.querySelector('[data-material-glaze-input]:checked');
    if (clayInput instanceof HTMLInputElement) clayInput.checked = anyCheckedGlazeInRow;
    if (anyCheckedGlazeInRow) anyRowHasGlaze = true;
  });

  // If there is at least one glaze selected on load and Clay is not explicitly
  // in the URL, trigger one re-render so the request includes Clay while the
  // visible URL remains glaze-only.
  if (!hasClayParam && anyRowHasGlaze) {
    const candidates = Array.from(document.querySelectorAll('facets-form-component'));
    /** @type {Element | undefined} */
    let targetFormComponent = candidates.find((fc) => {
      const form = fc.querySelector('form');
      return !!form && !!form.querySelector('[data-material-glaze-input], [name^="filter."]');
    });
    if (!targetFormComponent) {
      const fallback = document.querySelector('facets-form-component');
      targetFormComponent = fallback || undefined;
    }

    if (targetFormComponent && typeof /** @type any */ (targetFormComponent).updateFiltersByURL === 'function') {
      /** @type any */ (targetFormComponent).updateFiltersByURL(window.location.href);
      return;
    }

    const firstGlaze = document.querySelector('[data-material-glaze-input]');
    if (firstGlaze instanceof HTMLElement) {
      const evt = new Event('change', { bubbles: true });
      firstGlaze.dispatchEvent(evt);
    }
  }
});

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    // Use the closest section if available, otherwise fall back to the
    // main results-list section on the page.
    const closestSectionId = this.closest('.shopify-section')?.id;
    if (closestSectionId) return closestSectionId;

    const resultsListEl = document.querySelector('results-list');
    const resultsListId = resultsListEl?.getAttribute('section-id');
    if (resultsListId) return `shopify-section-${resultsListId}`;

    throw new Error('FacetInputs component must be a child of a section');
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    // Do not cache prefetch responses to avoid serving stale HTML when
    // filters are quickly toggled; we'll fetch fresh HTML on submit.
    sectionRenderer.getSectionHTML(this.sectionId, false, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
